import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useRegisterSocketRooms } from '../hooks/useSocketInvalidation';
import { useRecords, useRecordsBlocks, useSettings } from '../hooks/queries';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { hasFullAccess } from '../lib/permissions';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Download, Settings, Search, Phone, Edit2, Trash2, X, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, GripVertical, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_STATUS_OPTIONS = ['Pledge', 'Against', 'Undecided', 'NA', 'WN', 'SO', 'OC'];
const PLEDGE_STATUS_PRIORITY = ['Pledge', 'Against', 'Undecided', 'Not Voting', 'No answer', 'Meet candidate'];
const PAGE_SIZE = 50;

const sortPledgeStatusOptions = (options) => {
  const priorityLower = PLEDGE_STATUS_PRIORITY.map((p) => p.toLowerCase());
  return [...options].sort((a, b) => {
    const ai = priorityLower.indexOf((a || '').toLowerCase());
    const bi = priorityLower.indexOf((b || '').toLowerCase());
    if (ai >= 0 && bi >= 0) return ai - bi;
    if (ai >= 0) return -1;
    if (bi >= 0) return 1;
    return (a || '').localeCompare(b || '');
  });
};

const getStatusColor = (status) => {
  if (!status || status === 'Blank') return { bg: '#F3F4F6', text: '#6B7280' };
  if (status === 'Pledge') return { bg: '#D1FAE5', text: '#065F46' };
  if (status === 'Against') return { bg: '#FEE2E2', text: '#991B1B' };
  if (status === 'Undecided') return { bg: '#FEF3C7', text: '#92400E' };
  return { bg: '#E5E7EB', text: '#4B5563' };
};

// Status Cell Component
const StatusCell = React.memo(({ record, statusOptions, canEdit, onUpdate }) => {
  const colors = getStatusColor(record.pledge_status);
  
  if (canEdit) {
    return (
      <Select 
        value={record.pledge_status || 'Blank'} 
        onValueChange={(newValue) => onUpdate(record.record_id, newValue)}
      >
        <SelectTrigger 
          className="h-7 w-28 border-0 p-1 text-xs"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          <SelectValue>{record.pledge_status || '-'}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {statusOptions.map(opt => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
          <SelectItem value="Blank">Blank</SelectItem>
        </SelectContent>
      </Select>
    );
  }
  
  return (
    <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: colors.bg, color: colors.text }}>
      {record.pledge_status || '-'}
    </span>
  );
});

export default function RecordsPage() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const constituency_id = searchParams.get('constituency_id');
  const campaign_id = searchParams.get('campaign_id');

  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('__all__');
  const [blockFilter, setBlockFilter] = useState('__all__');
  const [votedFilter, setVotedFilter] = useState('__all__');
  
  const [currentPage, setCurrentPage] = useState(1);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Column settings dialog
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [editingColumnConfig, setEditingColumnConfig] = useState([]);
  const [newColumnName, setNewColumnName] = useState('');

  // Default column configuration
  const defaultColumns = useMemo(() => [
    { key: 'seq', label: 'Seq', order: 1, visible: true, editable: true, type: 'number' },
    { key: 'name', label: 'Name', order: 2, visible: true, editable: true, type: 'text' },
    { key: 'id_number', label: 'ID Number', order: 3, visible: true, editable: true, type: 'text' },
    { key: 'address', label: 'Address', order: 4, visible: true, editable: true, type: 'text' },
    { key: 'sex', label: 'Sex', order: 5, visible: true, editable: true, type: 'select', options: ['M', 'F'] },
    { key: 'contact_number', label: 'Contact', order: 6, visible: true, editable: true, type: 'text' },
    { key: 'island', label: 'Island', order: 7, visible: true, editable: true, type: 'text' },
    { key: 'block_number', label: 'Block', order: 8, visible: true, editable: true, type: 'text' },
    { key: 'vote_box', label: 'Vote Box', order: 9, visible: true, editable: true, type: 'text' },
    { key: 'box_name', label: 'Box Name', order: 10, visible: true, editable: true, type: 'text' },
    { key: 'age', label: 'Age', order: 11, visible: true, editable: true, type: 'number' },
    { key: 'pledge_status', label: 'Status', order: 12, visible: true, editable: true, type: 'status' },
    { key: 'voted', label: 'Voted', order: 13, visible: true, editable: false, type: 'voted' },
    { key: 'requests', label: 'Requests', order: 14, visible: false, editable: true, type: 'text' },
    { key: 'remarks', label: 'Remarks', order: 15, visible: true, editable: true, type: 'text' }
  ], []);

  useRegisterSocketRooms(constituency_id ? [`records:${constituency_id}`] : []);

  const { data: settings } = useSettings();
  const statusOptions = settings?.pledge_status_options ?? DEFAULT_STATUS_OPTIONS;
  const columnConfig = (settings?.column_config?.length > 0 ? settings.column_config : defaultColumns);

  const sortedStatusOptions = useMemo(
    () => sortPledgeStatusOptions(statusOptions),
    [statusOptions]
  );

  const { data: recordsData, isLoading: loading } = useRecords({
    constituency_id,
    campaign_id,
    page: currentPage,
    page_size: PAGE_SIZE,
    search: searchTerm,
    status_filter: statusFilter,
    block_filter: blockFilter,
    voted_filter: votedFilter,
  });

  const { data: blocksData } = useRecordsBlocks(constituency_id, campaign_id);

  const records = recordsData?.records ?? [];
  const totalRecords = recordsData?.total ?? 0;
  const totalPages = recordsData?.total_pages ?? 0;
  const blocks = blocksData?.blocks ?? [];

  const invalidateRecords = () => {
    queryClient.invalidateQueries({ queryKey: ['records'] });
  };

  const handleImport = async (file) => {
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post(
        `${API}/records/import?constituency_id=${constituency_id}&campaign_id=${campaign_id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      let msg = response.data.updated_count > 0
        ? `Imported ${response.data.imported_count} new, updated ${response.data.updated_count} existing`
        : `Imported ${response.data.imported_count} of ${response.data.total_rows} records`;
      if (response.data.added_statuses?.length > 0) {
        msg += `. New statuses added: ${response.data.added_statuses.join(', ')}`;
        queryClient.invalidateQueries({ queryKey: ['settings'] });
      }
      
      toast.success(msg);
      setCurrentPage(1);
      invalidateRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    try {
      toast.info('Preparing export...');
      const response = await api.get(
        `${API}/records/export?constituency_id=${constituency_id}&campaign_id=${campaign_id}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'records_export.xlsx';
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'No records to export' : 'Export failed');
    }
  };

  // Edit dialog handlers
  const openEditDialog = (record) => {
    setEditingRecord(record);
    setEditFormData({
      seq: record.seq || '',
      name: record.name || '',
      id_number: record.id_number || '',
      address: record.address || '',
      age: record.age || '',
      sex: record.sex || '',
      contact_number: record.contact_number || '',
      island: record.island || '',
      block_number: record.block_number || '',
      vote_box: record.vote_box || '',
      box_name: record.box_name || '',
      pledge_status: record.pledge_status || '',
      requests: (record.requests || []).join(', '),
      remarks: record.remarks || ''
    });
    setEditDialogOpen(true);
  };

  const saveEditForm = async () => {
    if (!editingRecord) return;
    
    try {
      const updateData = {
        ...editFormData,
        seq: parseInt(editFormData.seq) || 0,
        age: parseInt(editFormData.age) || 0,
        requests: editFormData.requests ? editFormData.requests.split(',').map(s => s.trim()) : []
      };
      
      await api.put(`${API}/records/${editingRecord.record_id}`, updateData);
      invalidateRecords();
      toast.success('Record updated');
      setEditDialogOpen(false);
      setEditingRecord(null);
    } catch (error) {
      toast.error('Failed to update record');
    }
  };

  const handleQuickStatusUpdate = async (recordId, newStatus) => {
    try {
      const updateData = { pledge_status: newStatus === 'Blank' ? '' : newStatus };
      await api.put(`${API}/records/${recordId}`, updateData);
      invalidateRecords();
      toast.success('Status updated');
    } catch (error) {
      toast.error('Update failed');
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await api.delete(`${API}/records/${recordId}`);
      invalidateRecords();
      toast.success('Record deleted');
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete all ${totalRecords} records? This cannot be undone.`)) return;
    try {
      const response = await api.delete(
        `${API}/records/bulk?constituency_id=${constituency_id}&campaign_id=${campaign_id}`,
      );
      toast.success(`Deleted ${response.data.count} records`);
      setCurrentPage(1);
      invalidateRecords();
    } catch (error) {
      toast.error('Failed to delete records');
    }
  };

  // Column settings handlers
  const openColumnSettings = () => {
    setEditingColumnConfig([...columnConfig]);
    setShowColumnSettings(true);
  };

  const saveColumnConfig = async () => {
    try {
      await api.put(`${API}/settings`, { column_config: editingColumnConfig });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      toast.success('Column settings saved');
      setShowColumnSettings(false);
    } catch (error) {
      toast.error('Failed to save column settings');
    }
  };

  const updateColumnLabel = (key, newLabel) => {
    setEditingColumnConfig(prev => prev.map(col => 
      col.key === key ? { ...col, label: newLabel } : col
    ));
  };

  const toggleColumnVisible = (key) => {
    setEditingColumnConfig(prev => prev.map(col => 
      col.key === key ? { ...col, visible: !col.visible } : col
    ));
  };

  const moveColumn = (index, direction) => {
    const newConfig = [...editingColumnConfig];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newConfig.length) return;
    
    [newConfig[index], newConfig[newIndex]] = [newConfig[newIndex], newConfig[index]];
    newConfig.forEach((col, i) => col.order = i + 1);
    setEditingColumnConfig(newConfig);
  };

  const addNewColumn = () => {
    if (!newColumnName.trim()) return;
    const key = newColumnName.toLowerCase().replace(/\s+/g, '_');
    if (editingColumnConfig.find(c => c.key === key)) {
      toast.error('Column already exists');
      return;
    }
    setEditingColumnConfig([...editingColumnConfig, {
      key,
      label: newColumnName,
      order: editingColumnConfig.length + 1,
      visible: true,
      editable: true,
      type: 'text',
      isCustom: true
    }]);
    setNewColumnName('');
  };

  const canEdit = hasFullAccess(user) || user?.effective_permissions?.includes('records.edit');
  const isSuperAdmin = hasFullAccess(user);
  
  const visibleColumns = columnConfig
    .filter(c => c.visible)
    .sort((a, b) => a.order - b.order);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const renderCell = (record, col) => {
    const value = col.key === 'requests' 
      ? (record[col.key] || []).join(', ')
      : record[col.key];
    
    if (col.key === 'pledge_status') {
      return (
        <StatusCell 
          record={record} 
          statusOptions={sortedStatusOptions} 
          canEdit={canEdit} 
          onUpdate={handleQuickStatusUpdate}
        />
      );
    }
    
    if (col.key === 'voted' || col.type === 'voted') {
      const isVoted = record.voted === true;
      return (
        <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs ${
          isVoted 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-500'
        }`} title={isVoted ? 'Voted' : 'Not voted'}>
          {isVoted ? '✓' : '—'}
        </span>
      );
    }
    
    if (col.key === 'contact_number' && value) {
      return (
        <a href={`tel:${value}`} className="text-blue-600 hover:underline flex items-center gap-1">
          <Phone className="h-3 w-3" />
          {value}
        </a>
      );
    }
    
    return <span className="truncate max-w-[150px] block" title={value || ''}>{value || '-'}</span>;
  };

  return (
    <Layout>
      <div className="p-4 md:p-8">
        <Button variant="ghost" onClick={() => navigate(`/campaigns?constituency_id=${constituency_id}`)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Campaigns
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Constituent Records
            </h1>
            <p className="text-muted-foreground text-sm">
              {totalRecords.toLocaleString()} total records
              {searchTerm && ` • Searching "${searchTerm}"`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 mt-3 md:mt-0">
            {isSuperAdmin && (
              <Button variant="outline" size="sm" onClick={openColumnSettings}>
                <Settings className="h-4 w-4 mr-1" />
                Columns
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
            {canEdit && (
              <>
                <input type="file" accept=".xlsx,.xls,.csv" id="file-upload" className="hidden" disabled={uploading}
                  onChange={(e) => { const file = e.target.files?.[0]; if (file) { handleImport(file); e.target.value = ''; } }}
                />
                <Button variant="outline" size="sm" disabled={uploading} onClick={() => document.getElementById('file-upload').click()}>
                  <Upload className="h-4 w-4 mr-1" />
                  {uploading ? 'Uploading...' : 'Import'}
                </Button>
              </>
            )}
            {isSuperAdmin && totalRecords > 0 && (
              <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search name, ID, address, phone..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="pl-10" />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-full md:w-36">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Statuses</SelectItem>
              {sortedStatusOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
              <SelectItem value="Blank">Blank</SelectItem>
            </SelectContent>
          </Select>
          <Select value={blockFilter} onValueChange={(v) => { setBlockFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-full md:w-36">
              <SelectValue placeholder="Block" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Blocks</SelectItem>
              {blocks.map(block => <SelectItem key={block} value={block}>{block}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={votedFilter} onValueChange={(v) => { setVotedFilter(v); setCurrentPage(1); }}>
            <SelectTrigger className="w-full md:w-36">
              <SelectValue placeholder="Voted" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="voted">Voted ✓</SelectItem>
              <SelectItem value="not_voted">Not Voted</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : records.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <h3 className="text-lg font-semibold mb-1">No records found</h3>
              <p className="text-muted-foreground text-sm">
                {searchTerm || statusFilter !== '__all__' || blockFilter !== '__all__' ? 'Try adjusting your filters' : 'Upload an Excel file to get started'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-lg border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary border-b">
                  <tr>
                    {visibleColumns.map(col => (
                      <th key={col.key} className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase whitespace-nowrap">
                        {col.label}
                      </th>
                    ))}
                    {canEdit && <th className="px-3 py-2 w-20 text-xs font-medium text-muted-foreground uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {records.map(record => (
                    <tr key={record.record_id} className="hover:bg-secondary/50 group">
                      {visibleColumns.map(col => (
                        <td key={col.key} className="px-3 py-2 text-sm">
                          {renderCell(record, col)}
                        </td>
                      ))}
                      {canEdit && (
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 hover:bg-blue-100" 
                              onClick={() => openEditDialog(record)}
                              data-testid="edit-record-btn"
                            >
                              <Edit2 className="h-4 w-4 text-blue-500" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-7 w-7 p-0 hover:bg-red-100" 
                              onClick={() => handleDeleteRecord(record.record_id)}
                              data-testid="delete-record-btn"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {records.map(record => {
                const colors = getStatusColor(record.pledge_status);
                return (
                  <Card key={record.record_id} className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">{record.name}</h3>
                        <p className="text-xs text-muted-foreground truncate">{record.address}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {record.voted && (
                          <span className="text-green-600" title="Voted">✓</span>
                        )}
                        <StatusCell record={record} statusOptions={sortedStatusOptions} canEdit={canEdit} onUpdate={handleQuickStatusUpdate} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div><span className="text-muted-foreground">ID:</span> {record.id_number}</div>
                      <div><span className="text-muted-foreground">Block:</span> {record.block_number}</div>
                      <div className="col-span-2">
                        <a href={`tel:${record.contact_number}`} className="text-blue-600">{record.contact_number}</a>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditDialog(record)}>
                          <Edit2 className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteRecord(record.record_id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-2">
                <div className="text-sm text-muted-foreground">Page {currentPage} of {totalPages}</div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => goToPage(1)} disabled={currentPage === 1}><ChevronsLeft className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="px-3 py-1 text-sm">{currentPage}</span>
                  <Button variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}><ChevronRight className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}><ChevronsRight className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Edit Record Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Record</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Seq</Label>
                <Input type="number" value={editFormData.seq || ''} onChange={(e) => setEditFormData({...editFormData, seq: e.target.value})} />
              </div>
              <div>
                <Label>Name</Label>
                <Input value={editFormData.name || ''} onChange={(e) => setEditFormData({...editFormData, name: e.target.value})} />
              </div>
              <div>
                <Label>ID Number</Label>
                <Input value={editFormData.id_number || ''} onChange={(e) => setEditFormData({...editFormData, id_number: e.target.value})} />
              </div>
              <div>
                <Label>Age</Label>
                <Input type="number" value={editFormData.age || ''} onChange={(e) => setEditFormData({...editFormData, age: e.target.value})} />
              </div>
              <div>
                <Label>Sex</Label>
                <Select value={editFormData.sex || '__none__'} onValueChange={(v) => setEditFormData({...editFormData, sex: v === '__none__' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-</SelectItem>
                    <SelectItem value="M">M</SelectItem>
                    <SelectItem value="F">F</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input value={editFormData.contact_number || ''} onChange={(e) => setEditFormData({...editFormData, contact_number: e.target.value})} />
              </div>
              <div className="col-span-2">
                <Label>Address</Label>
                <Input value={editFormData.address || ''} onChange={(e) => setEditFormData({...editFormData, address: e.target.value})} />
              </div>
              <div>
                <Label>Island</Label>
                <Input value={editFormData.island || ''} onChange={(e) => setEditFormData({...editFormData, island: e.target.value})} />
              </div>
              <div>
                <Label>Block</Label>
                <Input value={editFormData.block_number || ''} onChange={(e) => setEditFormData({...editFormData, block_number: e.target.value})} />
              </div>
              <div>
                <Label>Vote Box</Label>
                <Input value={editFormData.vote_box || ''} onChange={(e) => setEditFormData({...editFormData, vote_box: e.target.value})} />
              </div>
              <div>
                <Label>Box Name</Label>
                <Input value={editFormData.box_name || ''} onChange={(e) => setEditFormData({...editFormData, box_name: e.target.value})} />
              </div>
              <div>
                <Label>Pledge Status</Label>
                <Select value={editFormData.pledge_status || '__blank__'} onValueChange={(v) => setEditFormData({...editFormData, pledge_status: v === '__blank__' ? '' : v})}>
                  <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {sortedStatusOptions.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    <SelectItem value="__blank__">Blank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Requests</Label>
                <Input value={editFormData.requests || ''} onChange={(e) => setEditFormData({...editFormData, requests: e.target.value})} placeholder="Comma separated" />
              </div>
              <div className="col-span-2">
                <Label>Remarks</Label>
                <Input value={editFormData.remarks || ''} onChange={(e) => setEditFormData({...editFormData, remarks: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveEditForm}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Column Settings Dialog (Super Admin Only) */}
        <Dialog open={showColumnSettings} onOpenChange={setShowColumnSettings}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Column Settings</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              {editingColumnConfig.sort((a, b) => a.order - b.order).map((col, idx) => (
                <div key={col.key} className="flex items-center gap-2 p-2 bg-secondary rounded-lg">
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" className="h-4 w-4 p-0" onClick={() => moveColumn(idx, -1)} disabled={idx === 0}>▲</Button>
                    <Button size="sm" variant="ghost" className="h-4 w-4 p-0" onClick={() => moveColumn(idx, 1)} disabled={idx === editingColumnConfig.length - 1}>▼</Button>
                  </div>
                  <Checkbox checked={col.visible} onCheckedChange={() => toggleColumnVisible(col.key)} />
                  <Input 
                    value={col.label} 
                    onChange={(e) => updateColumnLabel(col.key, e.target.value)} 
                    className="flex-1 h-8"
                  />
                  <span className="text-xs text-muted-foreground w-20">{col.key}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Input placeholder="New column name..." value={newColumnName} onChange={(e) => setNewColumnName(e.target.value)} />
              <Button variant="outline" onClick={addNewColumn}><Plus className="h-4 w-4" /></Button>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowColumnSettings(false)}>Cancel</Button>
              <Button onClick={saveColumnConfig}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
